
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Order, Customer, User, OrderHistory, ProductComponent, InventoryMovement, InventoryMovementItem, AppSettings } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLocalization } from '../components/providers/LocalizationProvider';
import {
  Plus, Search, Filter, Clock, CheckCircle, Package, Truck, Home, Eye, EyeOff
} from "lucide-react";
import { format } from "date-fns";

import OrderPipeline from "../components/orders/OrderPipeline";
import OrderTable from "../components/orders/OrderTable";
import CreateOrderModal from "../components/orders/CreateOrderModal";
import OrderDetailsModal from "../components/orders/OrderDetailsModal";

export default function Orders() {
  const { t } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState("pipeline");
  const [productComponents, setProductComponents] = useState([]);
  const [appSettings, setAppSettings] = useState(null);
  const [paramOrderId, setParamOrderId] = useState(null);
  const [paramApplied, setParamApplied] = useState(false);

  // Read order_id from URL and store
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('order_id');
    if (id && id !== paramOrderId) {
      setParamOrderId(id);
      setParamApplied(false);
      // reset generic filters to ensure visibility
      setShowDeleted(false);
      setStatusFilter('all');
    }
  }, [location.search]);

  // When orders are loaded and paramOrderId is present, translate it into a search term
  useEffect(() => {
    if (paramOrderId && !paramApplied && Array.isArray(orders) && orders.length > 0) {
      const order = orders.find(o => o.id === paramOrderId);
      if (order) {
        setSearchTerm(order.order_number || order.id);
        // remove param from URL so user can clear search to reset
        navigate('/panel/orders', { replace: true });
      }
      setParamApplied(true);
      setParamOrderId(null);
    }
  }, [orders, paramOrderId, paramApplied]);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, statusFilter, showDeleted]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [ordersData, customersData, componentsData, settingsData] = await Promise.all([
        Order.list({ include_deleted: showDeleted }),
        Customer.list(),
        ProductComponent.list(),
        AppSettings.list(),
      ]);

      setOrders(ordersData);
      setCustomers(customersData);
      setProductComponents(componentsData);
      if (settingsData.length > 0) {
        setAppSettings(settingsData[0]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [showDeleted]);

  const filterOrders = () => {
    let filtered = orders;

    if (showDeleted) {
      filtered = filtered.filter(order => order.is_deleted === true);
    } else {
      filtered = filtered.filter(order => !order.is_deleted);
    }

    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    setFilteredOrders(filtered);
  };

  const manageShipmentForOrder = async (order, newStatus, oldStatus) => {
    console.log(`[manageShipmentForOrder] Order ${order.order_number}: ${oldStatus} -> ${newStatus}`);
    
    // ВСЕГДА ищем существующие накладные для этого заказа
    const existingMovements = await InventoryMovement.list({ order_id: order.id });
    console.log(`[manageShipmentForOrder] Found ${existingMovements.length} existing movements for order ${order.order_number}`);
    
    // Определяем нужный статус накладной на основе статуса заказа
    const getShipmentStatusFromOrderStatus = (orderStatus) => {
      switch (orderStatus) {
        case 'ordered': return 'draft'; // Заказан - черновик (не влияет на остатки)
        case 'paid': return 'payment_complete'; // Оплачен - завершена оплата
        case 'signed': return 'executed'; // Подписан - проведен
        case 'packed': return 'executed'; // Упакован - остается проведен
        case 'shipped': return 'executed'; // Отправлен - остается проведен
        case 'delivered': return 'executed'; // Доставлен - остается проведен
        default: return 'draft';
      }
    };

    const newShipmentStatus = getShipmentStatusFromOrderStatus(newStatus);
    const oldShipmentStatus = getShipmentStatusFromOrderStatus(oldStatus);

    console.log(`[manageShipmentForOrder] Shipment status should change: ${oldShipmentStatus} -> ${newShipmentStatus}`);

    // Если нужен статус накладной не изменился, ничего не делаем
    if (newShipmentStatus === oldShipmentStatus) {
      console.log(`[manageShipmentForOrder] Shipment status unchanged, skipping`);
      return;
    }

    // Если есть накладные - обновляем их статус
    if (existingMovements.length > 0) {
      // Обновляем ПЕРВУЮ накладную
      const firstMovement = existingMovements[0];
      await InventoryMovement.update(firstMovement.id, { status: newShipmentStatus });
      console.log(`[manageShipmentForOrder] Updated shipment ${firstMovement.document_number} status to ${newShipmentStatus}`);
      
      // Удаляем дубликаты, если они есть
      if (existingMovements.length > 1) {
        console.log(`[manageShipmentForOrder] Found ${existingMovements.length - 1} duplicate movements, deleting...`);
        for (let i = 1; i < existingMovements.length; i++) {
          await InventoryMovement.delete(existingMovements[i].id);
        }
      }
    } 
    // Если накладных нет и заказ переходит в статус, требующий накладную - создаем
    else if (['paid', 'signed', 'packed', 'shipped', 'delivered'].includes(newStatus)) {
      console.log(`[manageShipmentForOrder] No existing movements, creating new shipment with status ${newShipmentStatus}`);
      await createShipmentForOrder(order, newShipmentStatus);
    }
  };

  const handleOrderCreate = async (orderData) => {
    await Order.create(orderData);
    await loadData();
    setShowCreateModal(false);
  };

  const createShipmentForOrder = async (order, initialStatus = 'payment_complete') => {
    console.log(`[createShipmentForOrder] Creating shipment for order ${order.order_number} with status ${initialStatus}`);
    
    // ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: еще раз убеждаемся, что накладной нет
    const doubleCheckMovements = await InventoryMovement.list({ order_id: order.id });
    if (doubleCheckMovements.length > 0) {
        console.log(`[createShipmentForOrder] ABORT: Found existing movements during double-check, will not create duplicate`);
        return;
    }

    if (!appSettings || !appSettings.store_settings?.storefront_warehouse_ids?.length) {
        console.error("Не настроен склад для отгрузки в настройках витрины.");
        alert("Ошибка: не настроен склад для отгрузки. Проверьте настройки витрины.");
        return;
    }
    const fromWarehouseId = appSettings.store_settings.storefront_warehouse_ids[0];

    // Use a Map to aggregate quantities by item_id
    const aggregatedItems = new Map();

    for (const orderItem of order.items) {
        const components = productComponents.filter(c => c.product_id === orderItem.product_id);
        for (const component of components) {
            const currentQuantity = aggregatedItems.get(component.item_id) || 0;
            aggregatedItems.set(component.item_id, currentQuantity + (component.quantity * orderItem.quantity));
        }
    }

    if (aggregatedItems.size === 0) {
      console.log(`[createShipmentForOrder] No components found for order ${order.order_number}. No shipment created.`);
      return;
    }

    const movementItems = Array.from(aggregatedItems).map(([item_id, quantity]) => ({
        item_id,
        quantity
    }));

    await InventoryMovement.create({
        document_number: `SHIP-${order.order_number}`,
        document_date: new Date().toISOString().split('T')[0],
        document_type: 'shipment',
        type: 'out',
        warehouse_id: fromWarehouseId,
        basis: `Заказ #${order.order_number}`,
        status: initialStatus, // Используем переданный статус
        notes: `Автоматическая отгрузка для заказа.`,
        order_id: order.id,
        items: movementItems,
    });
    console.log(`[createShipmentForOrder] Successfully created shipment ${movement.document_number} with status ${initialStatus} for order ${order.order_number}`);
  };

  const handleOrderUpdate = async (orderId, updates) => {
    const originalOrder = orders.find(o => o.id === orderId);
    
    // Если статус изменился, управляем связанной накладной
    if (updates.status && updates.status !== originalOrder?.status) {
        const fullOrderData = { ...originalOrder, ...updates };
        await manageShipmentForOrder(fullOrderData, updates.status, originalOrder.status);
    }
    
    await Order.update(orderId, updates);
    await loadData();
    if (selectedOrder && selectedOrder.id === orderId) {
      const updatedOrder = await Order.get(orderId);
      setSelectedOrder(updatedOrder);
    }
  };

  const handleOrderStatusDrop = async (orderId, newStatus) => {
    const orderToUpdate = orders.find(o => o.id === orderId);
    if (!orderToUpdate || orderToUpdate.status === newStatus) return;
    
    const oldStatus = orderToUpdate.status;
    const updatedOrders = orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o);
    setOrders(updatedOrders);

    try {
      await Order.update(orderId, { status: newStatus });
      await manageShipmentForOrder(orderToUpdate, newStatus, oldStatus);

      const user = await User.me();
      await OrderHistory.create({
        order_id: orderId,
        action: 'status_changed',
        details: t('components.order_details.action_details.status_changed_dnd', { oldValue: oldStatus, newValue: newStatus }),
        old_value: oldStatus,
        new_value: newStatus,
        user_name: user.full_name
      });
    } catch (error) {
      console.error("Failed to update order status:", error);
      setOrders(orders); // Revert
    }
    await loadData();
  };

  const handleOrderDelete = async (orderId) => {
    await Order.delete(orderId);
    await loadData();
    setShowDetailsModal(false);
  };

  const handleOrderRestore = async (orderId) => {
    await Order.restore(orderId);
    await loadData();
    setShowDetailsModal(false);
  };

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const statusIcons = { ordered: Clock, paid: CheckCircle, signed: CheckCircle, packed: Package, shipped: Truck, delivered: Home };

  return (
    <div className="min-h-screen gradient-bg p-6">
      <div className="w-full space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
                    <h1 className="text-3xl font-bold text-slate-900">{t('orders.title')}</h1>
        <p className="text-slate-600 font-medium mt-1">{t('orders.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowCreateModal(true)} className="bg-amber-600 hover:bg-amber-700 text-white font-medium px-6">
              <Plus className="w-4 h-4 mr-2" />
              {t('orders.new_order')}
            </Button>
          </div>
        </div>

        <Card className="elegant-card">
          <CardContent className="p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder={t('orders.search_placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                  />
                </div>
                <div className="flex gap-2">
                  {['all', 'ordered', 'paid', 'packed', 'shipped', 'delivered'].map((status) => {
                    const Icon = status === 'all' ? Filter : statusIcons[status];
                    const count = status === 'all' ? filteredOrders.length : filteredOrders.filter(o => o.status === status).length;
                    return (
                      <Button key={status} variant={statusFilter === status ? "default" : "outline"} onClick={() => setStatusFilter(status)} className={`flex items-center gap-2 ${statusFilter === status ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-slate-200 hover:border-amber-300'}`}>
                        <Icon className="w-4 h-4" />
                        {status === 'all' ? t('general.all') : status.charAt(0).toUpperCase() + status.slice(1)}
                        <Badge variant="secondary" className="ml-1 bg-slate-100 text-slate-600">{count}</Badge>
                      </Button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                <div className="flex items-center space-x-3">
                  <Switch checked={showDeleted} onCheckedChange={setShowDeleted} id="show-deleted" />
                  <Label htmlFor="show-deleted" className="flex items-center gap-2 font-medium text-slate-700">
                    {showDeleted ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showDeleted ? t('orders.show_deleted') : t('orders.show_active')}
                  </Label>
                </div>
                <div className="text-sm text-slate-500">{t('orders.total_orders', { count: filteredOrders.length })}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 bg-slate-100">
            <TabsTrigger value="pipeline" className="data-[state=active]:bg-white">{t('orders.pipeline_view')}</TabsTrigger>
            <TabsTrigger value="table" className="data-[state=active]:bg-white">{t('orders.table_view')}</TabsTrigger>
          </TabsList>

          <TabsContent value="pipeline" className="mt-6">
            <OrderPipeline 
              orders={filteredOrders} 
              onOrderSelect={handleOrderSelect} 
              onOrderStatusDrop={handleOrderStatusDrop} 
              onOrderDelete={handleOrderDelete}
              onOrderRestore={handleOrderRestore}
              showDeleted={showDeleted}
              isLoading={isLoading} 
            />
          </TabsContent>
          <TabsContent value="table" className="mt-6">
            <OrderTable 
              orders={filteredOrders} 
              onOrderSelect={handleOrderSelect} 
              onOrderDelete={handleOrderDelete}
              onOrderRestore={handleOrderRestore}
              showDeleted={showDeleted}
              isLoading={isLoading} 
            />
          </TabsContent>
        </Tabs>

        <CreateOrderModal 
          isOpen={showCreateModal} 
          onClose={() => setShowCreateModal(false)} 
          onSubmit={handleOrderCreate} 
          customers={customers}
        />
        <OrderDetailsModal 
          isOpen={showDetailsModal} 
          onClose={() => setShowDetailsModal(false)} 
          order={selectedOrder} 
          onUpdate={handleOrderUpdate} 
          onDelete={handleOrderDelete} 
          onRestore={handleOrderRestore} 
          showDeleted={showDeleted} 
        />
      </div>
    </div>
  );
}
